import { useRef } from 'react';
import { create } from 'zustand';

import { SectionDto } from '~modules/section/model';
import { changeUsersSections, loadUser, setSectionId } from '~modules/user/api';
import { UserDto } from '~modules/user/model';

export const USERS_LIST_LIMIT = 100;

type UsersStoreState = {
	search: string;
	isPending: boolean;
	list: UserDto[];
	page: number;
	total: number;
	selected: number[];
	load: (val: string, page: number) => void;
	onChangeSection: (id: number, section: SectionDto) => void;
	onSelect: (id: number) => void;
	onSelectAll: () => void;
	onChangeList: (section: SectionDto) => Promise<void>;
};

const usersStore = create<UsersStoreState>((set, get) => ({
	search: '',
	isPending: true,
	list: [],
	page: 0,
	total: 0,
	selected: [],
	load: async (val: string, page: number) => {
		set({ search: val, isPending: true, list: [], page: 0, selected: [] });
		const { total, list } = await loadUser(val, USERS_LIST_LIMIT, page * USERS_LIST_LIMIT);
		set({ isPending: false, list, total, page, selected: [] });
	},
	onChangeSection: (id: number, section: SectionDto) => {
		const { list } = get();
		setSectionId(id, section.id!);
		set({ list: list.map(itm => (itm.id === id ? { ...itm, section: section, section_id: section.id } : itm)) });
	},
	onSelect: (id: number) => {
		const { selected } = get();
		set({ selected: selected.includes(id) ? selected.filter(itm => itm !== id) : [...selected, id] });
	},
	onSelectAll: () => {
		const { selected, list } = get();
		if (selected.length === list.length) {
			set({ selected: [] });
		} else {
			set({ selected: list.map(itm => itm.id!) });
		}
	},
	onChangeList: async (section: SectionDto) => {
		const { selected, search, page } = get();
		set({ isPending: true, list: [], page: 0, total: 0, selected: [] });
		await changeUsersSections(section.id!, selected);
		const { total, list } = await loadUser(search, USERS_LIST_LIMIT, page * USERS_LIST_LIMIT);
		set({ isPending: false, list, total, page, selected: [] });
	},
}));

export function useUsers() {
	const {
		search,
		isPending,
		list,
		page,
		total,
		selected,
		load,
		onChangeSection,
		onSelect,
		onSelectAll,
		onChangeList,
	} = usersStore();
	const ref = useRef(false);

	if (!ref.current && isPending) {
		ref.current = true;
		load('', 0);
	}

	const onSearch = (val: string) => {
		load(val, 0);
	};

	const onChangePage = (val: number) => {
		load(search, val);
	};

	return {
		search,
		list,
		isPending,
		page,
		total,
		selected,
		onSearch,
		onChangeSection,
		onChangePage,
		onSelect,
		onSelectAll,
		onChangeList,
	};
}
